import type {
  GameHubUser,
  GameMetadata,
  GameSaveState,
  HubStarsProduct,
  HubReferralStatus,
  OneLifeState,
  LifeEvent,
  LifeChoice,
  LifeSummary,
} from '@gamengayxua/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

class GameHubApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('gnx_hub_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('gnx_hub_token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('gnx_hub_token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Lỗi hệ thống (${response.status})`);
    }

    return data as T;
  }

  // --- AUTH ---
  async loginTelegram(initData: string, startParam?: string): Promise<{ user: GameHubUser; token: string }> {
    const res = await this.request<{ user: GameHubUser; token: string }>('/api/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData, startParam }),
    });
    this.setToken(res.token);
    return res;
  }

  async loginDemo(username?: string, telegramId?: number): Promise<{ user: GameHubUser; token: string }> {
    const res = await this.request<{ user: GameHubUser; token: string }>('/api/auth/demo', {
      method: 'POST',
      body: JSON.stringify({ username, telegramId }),
    });
    this.setToken(res.token);
    return res;
  }

  // --- HUB PROFILE & GAMES ---
  async getProfile(): Promise<{ user: GameHubUser; saves: any[] }> {
    return this.request<{ user: GameHubUser; saves: any[] }>('/api/hub/profile');
  }

  async getGames(): Promise<{ games: GameMetadata[]; categories: { id: string; name: string }[] }> {
    return this.request<{ games: GameMetadata[]; categories: { id: string; name: string }[] }>('/api/hub/games');
  }

  async getGameSave(gameId: string): Promise<{ save: GameSaveState | null }> {
    return this.request<{ save: GameSaveState | null }>(`/api/hub/save/${gameId}`);
  }

  async saveGameProgress(gameId: string, data: Record<string, any>, summaryText?: string): Promise<{ success: boolean; save: GameSaveState }> {
    return this.request<{ success: boolean; save: GameSaveState }>(`/api/hub/save/${gameId}`, {
      method: 'POST',
      body: JSON.stringify({ data, summaryText }),
    });
  }

  async getLeaderboard(): Promise<{ leaderboard: any[] }> {
    return this.request<{ leaderboard: any[] }>('/api/hub/leaderboard');
  }

  async getDailyQuests(): Promise<{ quests: any[] }> {
    return this.request<{ quests: any[] }>('/api/hub/daily-quests');
  }

  // --- ONE LIFE (MỘT KIẾP NGƯỜI) ---
  async birthOneLife(params?: { countryCode?: string; characterName?: string; gender?: 'male' | 'female'; isFastMode?: boolean }): Promise<{
    destiny: OneLifeState;
    availableEvents: LifeEvent[];
  }> {
    return this.request<{ destiny: OneLifeState; availableEvents: LifeEvent[] }>('/api/onelife/birth', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  async makeOneLifeChoice(params: { state: OneLifeState; event: LifeEvent; choice: LifeChoice }): Promise<{
    updatedState: OneLifeState;
    isDeath: boolean;
    causeOfDeath?: string;
    availableEvents: LifeEvent[];
  }> {
    return this.request<{
      updatedState: OneLifeState;
      isDeath: boolean;
      causeOfDeath?: string;
      availableEvents: LifeEvent[];
    }>('/api/onelife/choice', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async completeOneLife(state: OneLifeState): Promise<{ summary: LifeSummary }> {
    return this.request<{ summary: LifeSummary }>('/api/onelife/complete', {
      method: 'POST',
      body: JSON.stringify({ state }),
    });
  }

  async getMyLives(): Promise<{ records: LifeSummary[] }> {
    return this.request<{ records: LifeSummary[] }>('/api/onelife/my-lives');
  }

  async getShareCard(shareCode: string): Promise<{ summary: LifeSummary }> {
    return this.request<{ summary: LifeSummary }>(`/api/onelife/share/${shareCode}`);
  }

  // --- STARS SHOP ---
  async getStarsProducts(): Promise<{ products: HubStarsProduct[]; currency: string; mode: string }> {
    return this.request<{ products: HubStarsProduct[]; currency: string; mode: string }>('/api/stars/products');
  }

  async createStarsInvoice(productId: string): Promise<{
    success: boolean;
    orderId: string;
    invoiceLink: string;
    starsAmount: number;
    product: HubStarsProduct;
  }> {
    return this.request<{
      success: boolean;
      orderId: string;
      invoiceLink: string;
      starsAmount: number;
      product: HubStarsProduct;
    }>('/api/stars/invoice', {
      method: 'POST',
      body: JSON.stringify({ productId }),
    });
  }

  // --- REFERRAL ---
  async getReferralStatus(): Promise<HubReferralStatus> {
    return this.request<HubReferralStatus>('/api/referral/status');
  }

  async claimReferralReward(milestoneIndex: number): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/referral/claim', {
      method: 'POST',
      body: JSON.stringify({ milestoneIndex }),
    });
  }
}

export const api = new GameHubApiClient();
